import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  ExitToApp,
  Home,
  EmojiEvents,
  Timer,
  Leaderboard,
  ChevronLeft,
  ChevronRight,
  ArrowBack,
} from '@mui/icons-material'
import { SessionNodeDto } from '@api/types/session'
import { WorkflowNodeType } from '../../../types/workflow.types'
import { useSessionStore } from '../../../stores/sessionStore'
import { parseTimeSpan, formatTime } from '../../../hooks/useElapsedTimer'
import { useSessionJournal } from '../../../hooks/useSessionJournal'
import { JournalEntryContent, VISIBLE_ENTRY_TYPES } from '../journal/JournalPanel'

interface ExitNodeProps {
  node: SessionNodeDto
}

export default function ExitNode({ node }: ExitNodeProps) {
  const navigate = useNavigate()
  const store = useSessionStore()
  const summary = store.completionSummary
  const nodeType = node.nodeType as WorkflowNodeType
  const [showJournal, setShowJournal] = useState(false)

  const isSuccess = nodeType === WorkflowNodeType.ExitSuccess
  const isFailed = nodeType === WorkflowNodeType.ExitFailed
  const isAbandoned = nodeType === WorkflowNodeType.ExitAbandoned

  const handleGoHome = () => {
    store.reset()
    navigate('/my-adventures')
  }

  // Determine icon and color based on exit type
  let Icon = ExitToApp
  let color = 'primary.main'
  let title = 'Adventure Complete'
  let message = 'You have completed this adventure.'

  if (isSuccess) {
    Icon = CheckCircle
    color = 'success.main'
    title = 'Congratulations!'
    message = 'You have successfully completed this adventure!'
  } else if (isFailed) {
    Icon = Cancel
    color = 'error.main'
    title = 'Adventure Failed'
    message = 'Unfortunately, you did not complete this adventure.'
  } else if (isAbandoned) {
    Icon = ExitToApp
    color = 'warning.main'
    title = 'Adventure Abandoned'
    message = 'You have left this adventure.'
  }

  // Use node label/description if provided
  if (node.label && node.label !== 'Exit') {
    title = node.label
  }
  if (node.description) {
    message = node.description
  }

  if (showJournal) {
    return (
      <JournalView
        sessionId={store.sessionId}
        onBack={() => setShowJournal(false)}
      />
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        px: 2,
        overflowY: 'auto',
        flex: 1,
      }}
    >
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ py: 4 }}>
          {/* Status Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {!node.exitMessage && (
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: isSuccess ? 'success.light' : isFailed ? 'error.light' : 'warning.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Icon sx={{ fontSize: 56, color }} />
              </Box>
            )}

            {node.exitMessage ? (
              <Box
                sx={{
                  textAlign: 'left',
                  '& p': { my: 0.5 },
                  '& img': { maxWidth: '100%', borderRadius: 1 },
                  '& a': { color: 'primary.main' },
                  '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
                  '& td, & th': { p: 1, verticalAlign: 'top' },
                  '& table:not([data-borderless="true"]) td, & table:not([data-borderless="true"]) th': { border: '1px solid', borderColor: 'divider' },
                  '& table:not([data-borderless="true"]) th': { bgcolor: 'action.hover', fontWeight: 600 },
                }}
                dangerouslySetInnerHTML={{ __html: node.exitMessage }}
              />
            ) : (
              <>
                <Typography variant="h4" gutterBottom sx={{ color }}>
                  {title}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                  {message}
                </Typography>
              </>
            )}
          </Box>

          {/* Time Breakdown */}
          {summary?.timeBreakdown && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Timer fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Time Breakdown
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary.main">
                      {formatTime(parseTimeSpan(summary.timeBreakdown.wallDuration))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Time
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary.main">
                      {formatTime(parseTimeSpan(summary.timeBreakdown.effectiveDuration))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Score Time
                    </Typography>
                  </Box>
                </Box>

                {/* Breakdown items */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {summary.timeBreakdown.pausedSeconds > 0 && (
                    <TimeBreakdownItem
                      label="Paused"
                      seconds={summary.timeBreakdown.pausedSeconds}
                    />
                  )}
                  {summary.timeBreakdown.timeModifierSeconds !== 0 && (
                    <TimeBreakdownItem
                      label="Time Modifiers"
                      seconds={summary.timeBreakdown.timeModifierSeconds}
                      showSign
                    />
                  )}
                  {summary.timeBreakdown.cluePenaltySeconds > 0 && (
                    <TimeBreakdownItem
                      label={`Clue Penalties (${summary.timeBreakdown.cluesUsed} clue${summary.timeBreakdown.cluesUsed !== 1 ? 's' : ''} used)`}
                      seconds={summary.timeBreakdown.cluePenaltySeconds}
                      showSign
                    />
                  )}
                  {summary.timeBreakdown.carriedOverPenaltySeconds > 0 && (
                    <TimeBreakdownItem
                      label="Carried Over Penalty"
                      seconds={summary.timeBreakdown.carriedOverPenaltySeconds}
                      showSign
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

          {/* Leaderboard */}
          {summary?.leaderboard && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Leaderboard fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Leaderboard
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      #{summary.leaderboard.position}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {summary.leaderboard.totalPlayers} player{summary.leaderboard.totalPlayers !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="text.secondary">
                      {summary.leaderboard.bestTimeFormatted}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Best Time
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Achievements */}
          {summary?.achievements && summary.achievements.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EmojiEvents fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Achievements
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {summary.achievements.map((achievement) => (
                    <Box key={achievement.achievementId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        {achievement.iconUrl ? (
                          <Box
                            component="img"
                            src={achievement.iconUrl}
                            alt=""
                            sx={{ width: 32, height: 32, borderRadius: 1 }}
                          />
                        ) : (
                          <EmojiEvents
                            sx={{
                              fontSize: 32,
                              color: achievement.isCompleted ? 'warning.main' : 'action.disabled',
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {achievement.achievementName}
                            {achievement.isCompleted && ' \u2713'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {achievement.description}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {achievement.completedCount}/{achievement.totalAdventureCount}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={achievement.completionPercentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: achievement.isCompleted ? 'warning.main' : 'primary.main',
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Collections */}
          {summary?.collections && summary.collections.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Collections
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {summary.collections.map((collection) => (
                    <Box key={collection.collectionId}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {collection.collectionName}
                          {collection.isCompleted && ' \u2713'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {collection.completedAchievementCount}/{collection.totalAchievementCount}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={collection.completionPercentage}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: collection.isCompleted ? 'success.main' : 'primary.main',
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Fallback stats when no completion summary */}
          {!summary && store.progressPercent > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 4,
                  py: 1,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {Math.round(store.progressPercent)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Progress
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {formatTime(store.elapsedSeconds)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Time
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
            >
              Return to My Adventures
            </Button>

            <Button
              variant="outlined"
              onClick={() => setShowJournal(true)}
            >
              View Journal
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

const ENTRIES_PER_PAGE = 3

function JournalView({
  sessionId,
  onBack,
}: {
  sessionId: string | null
  onBack: () => void
}) {
  const { journal, isLoading } = useSessionJournal(sessionId)
  const [currentPage, setCurrentPage] = useState(0)
  const [playingAudio, setPlayingAudio] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const visibleEntries = useMemo(() => {
    return journal.filter(entry => VISIBLE_ENTRY_TYPES.includes(entry.entryType))
  }, [journal])

  const totalPages = Math.max(1, Math.ceil(visibleEntries.length / ENTRIES_PER_PAGE))

  const currentPageEntries = useMemo(() => {
    const start = currentPage * ENTRIES_PER_PAGE
    return visibleEntries.slice(start, start + ENTRIES_PER_PAGE)
  }, [visibleEntries, currentPage])

  // Keep page in bounds
  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1))
    }
  }, [visibleEntries.length, currentPage, totalPages])

  const handlePlayAudio = (url: string, entryId: string) => {
    if (playingAudio === entryId) {
      audioRef.current?.pause()
      setPlayingAudio(null)
    } else {
      if (audioRef.current) {
        audioRef.current.src = url
        audioRef.current.play()
        setPlayingAudio(entryId)
      }
    }
  }

  // no-op for video in read-only mode
  const handleOpenVideo = (_url: string, _title: string) => {}

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Notebook styling — matches JournalNotebook
        background: 'linear-gradient(to right, #e8e0d0 0%, #f5f0e6 3%, #faf8f3 5%, #faf8f3 95%, #f0ebe1 97%, #e8e0d0 100%)',
        position: 'relative',
      }}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={() => setPlayingAudio(null)} />

      {/* Header — sits above the content area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: '10%',
          py: 1.5,
          background: 'rgba(255,255,255,0.3)',
        }}
      >
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => {
            audioRef.current?.pause()
            setPlayingAudio(null)
            onBack()
          }}
          sx={{
            mr: 1,
            color: '#555',
            textTransform: 'none',
          }}
        >
          Summary
        </Button>
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Caveat", "Segoe Script", cursive',
            fontSize: '1.5rem',
            color: '#333',
            flex: 1,
            textAlign: 'center',
            mr: 8, // offset for centering against back button
          }}
        >
          Journal
        </Typography>
      </Box>

      {/* Journal page content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          pl: '10%',
          pr: '5%',
          py: 3,
          position: 'relative',
          // Lined paper effect
          backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, rgba(0,0,0,0.04) 32px)',
          backgroundSize: '100% 32px',
          // Spine shadow
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '3%',
            top: 0,
            bottom: 0,
            width: 4,
            background: 'linear-gradient(to right, rgba(0,0,0,0.15), transparent)',
            zIndex: 1,
          },
          // Red margin line
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '8%',
            top: 0,
            bottom: 0,
            width: 2,
            background: 'rgba(200, 50, 50, 0.3)',
            zIndex: 1,
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : visibleEntries.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'text.secondary',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Caveat", "Segoe Script", cursive',
                fontSize: '1.2rem',
                fontStyle: 'italic',
              }}
            >
              Your journal is empty...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"Caveat", "Segoe Script", cursive',
                fontSize: '1rem',
                mt: 1,
              }}
            >
              No entries were recorded during this adventure
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentPageEntries.map((entry) => (
              <JournalEntryContent
                key={entry.id}
                entry={entry}
                playingAudio={playingAudio}
                onPlayAudio={handlePlayAudio}
                onOpenVideo={handleOpenVideo}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Page navigation */}
      {visibleEntries.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '10%',
            py: 1,
            bgcolor: 'transparent',
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            sx={{
              p: 0.25,
              color: 'rgba(0,0,0,0.35)',
              '&:hover': { color: 'rgba(0,0,0,0.6)', bgcolor: 'transparent' },
              '&.Mui-disabled': { color: 'rgba(0,0,0,0.15)' },
            }}
          >
            <ChevronLeft sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            sx={{
              fontFamily: '"Caveat", "Segoe Script", cursive',
              fontSize: '0.8rem',
              color: 'rgba(0,0,0,0.4)',
              minWidth: 40,
              textAlign: 'center',
            }}
          >
            {currentPage + 1}/{totalPages}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
            disabled={currentPage >= totalPages - 1}
            sx={{
              p: 0.25,
              color: 'rgba(0,0,0,0.35)',
              '&:hover': { color: 'rgba(0,0,0,0.6)', bgcolor: 'transparent' },
              '&.Mui-disabled': { color: 'rgba(0,0,0,0.15)' },
            }}
          >
            <ChevronRight sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

function TimeBreakdownItem({
  label,
  seconds,
  showSign = false,
}: {
  label: string
  seconds: number
  showSign?: boolean
}) {
  const prefix = showSign && seconds > 0 ? '+' : ''
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {prefix}{formatTime(Math.abs(seconds))}
      </Typography>
    </Box>
  )
}
